import {
  PLANNED_MEDICATION_FIELD_LABEL,
  TRANSFER_RECEPT_PLAN_CHOICES,
  TRANSFER_RECEPT_PRESCRIPTION_NEEDS,
  TRANSFER_RECEPT_PRESCRIPTION_NEED_LABEL,
  TRANSFER_RECEPT_REPORTED_TAG,
  TRANSFER_RECEPT_SAVED_WITH_COMPLETION,
  type PlannedMedicationView,
} from "@care-by-ella/contracts";
import { fieldDomId } from "@/lib/form-errors";
import {
  receptPlanChoiceLabels,
  withPlanChoice,
  type ReportedTreatment,
  type TransferReceptValue,
} from "@/lib/transfer-recept";
import {
  PrescriptionOrderFields,
  type OrderFieldErrors,
} from "./PrescriptionOrderFields";
import { PlannedMedicationExtraFields } from "./PlannedMedicationExtraFields";
import { PlannedMedicationSummary } from "./PlannedMedicationSummary";

// TES-1241 (TES-1240) — "Recept-sektionen är obligatorisk och förifylld"
// (A-02 2026-10-11 §4). The transfer review's structured Recept section:
// her F-07 answers, tagged as hers until the doctor has chosen; the
// planned medication in the care meeting's own fields (PrescriptionOrderFields
// + PlannedMedicationExtraFields, and PlannedMedicationSummary when a plan
// already exists); and Emelie's four choices as the two questions they
// answer. The approval saves what stands here as the planned medication.

function fmt(date: string): string {
  return new Date(date).toLocaleString("sv-SE", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

const MISSING = "Saknar information";

const NEED_HINT: Record<
  keyof typeof TRANSFER_RECEPT_PRESCRIPTION_NEED_LABEL,
  string
> = {
  existing_valid:
    "Inget nytt e-recept. Receptstatusen bekräftas när du godkänner receptövertaget.",
  new_e_prescription:
    "Utfärda e-receptet i Webdoc och bekräfta det när du godkänner receptövertaget.",
};

export const RECEPT_SECTION_ID = "transfer-recept";

export function TransferReceptSection({
  idPrefix,
  formId,
  reported,
  prescriptionDocuments,
  planned,
  value,
  onChange,
  disabled,
  errors,
  returning = false,
}: {
  // TES-1242 — back after kompletteringar with a saved plan: Emelie's
  // "Behandlingen gäller fortfarande" / "Ändra behandlingen".
  returning?: boolean;
  idPrefix: string;
  formId: string;
  reported: ReportedTreatment | null | undefined;
  // The prescription or order documents she uploaded with F-07.
  prescriptionDocuments: number;
  planned: PlannedMedicationView | null;
  value: TransferReceptValue;
  onChange: (next: TransferReceptValue) => void;
  disabled?: boolean;
  errors: OrderFieldErrors & {
    plannedTreatment?: string;
    changeReason?: string;
    prescriptionNeed?: string;
  };
}) {
  const current = planned?.current ?? null;
  const choiceLabels = receptPlanChoiceLabels(returning);
  const editable = value.planChoice === "changed" && !disabled;
  const rows: [string, string | null][] = [
    [PLANNED_MEDICATION_FIELD_LABEL.medication, reported?.medication ?? null],
    [PLANNED_MEDICATION_FIELD_LABEL.strength, reported?.strength ?? null],
    [PLANNED_MEDICATION_FIELD_LABEL.dose, reported?.dose ?? null],
    [PLANNED_MEDICATION_FIELD_LABEL.frequency, reported?.frequency ?? null],
    ["Behandlingsstart", reported?.treatmentStart ?? null],
    ["Receptets giltighet", reported?.prescriptionValidUntil ?? null],
    [
      "Recept- eller ordinationsunderlag",
      prescriptionDocuments > 0
        ? `${prescriptionDocuments} ${prescriptionDocuments === 1 ? "bilaga" : "bilagor"}`
        : null,
    ],
  ];

  return (
    <section
      id={RECEPT_SECTION_ID}
      tabIndex={-1}
      aria-label="Recept"
      className="mb-lg flex flex-col gap-sm rounded-md border border-surfaceMuted p-md"
      data-testid="transfer-recept"
    >
      <h3 className="font-display text-lg">Recept</h3>

      <div className="rounded-md bg-surfaceMuted/40 p-sm">
        <div className="mb-xs flex flex-wrap items-baseline gap-sm">
          <span className="text-xs font-semibold text-textSecondary">
            F-07 Receptövertag
            {reported?.submittedAt
              ? ` • inskickad ${fmt(reported.submittedAt)}`
              : ""}
          </span>
          <span
            className="rounded border border-warning/50 px-xs py-[2px] text-xs font-medium text-warning"
            data-testid="transfer-recept-reported-tag"
          >
            {TRANSFER_RECEPT_REPORTED_TAG}
          </span>
        </div>
        <dl className="grid grid-cols-[auto_1fr] gap-x-sm gap-y-[2px] text-sm">
          {rows.map(([label, v]) => (
            <div key={label} className="contents">
              <dt className="text-textSecondary">{label}</dt>
              <dd className={v ? "" : "text-textSecondary"}>{v ?? MISSING}</dd>
            </div>
          ))}
        </dl>
      </div>

      <fieldset
        id={fieldDomId(formId, "plannedTreatment")}
        tabIndex={-1}
        className="flex flex-col gap-xs"
      >
        <legend className="mb-xs text-sm font-semibold">
          Planerad läkemedelsbehandling
        </legend>
        {current ? (
          <PlannedMedicationSummary
            current={current}
            history={planned?.versions ?? []}
          />
        ) : null}
        <div role="radiogroup" className="flex flex-wrap gap-md">
          {TRANSFER_RECEPT_PLAN_CHOICES.map((c) => (
            <label key={c} className="flex items-center gap-sm text-sm">
              <input
                type="radio"
                name={`${idPrefix}-planned-treatment`}
                className="accent-brand"
                checked={value.planChoice === c}
                disabled={disabled}
                onChange={() =>
                  onChange(withPlanChoice(value, c, reported, current))
                }
              />
              {choiceLabels[c]}
            </label>
          ))}
        </div>
        {errors.plannedTreatment ? (
          <p className="text-xs text-danger">{errors.plannedTreatment}</p>
        ) : null}
        <PrescriptionOrderFields
          idPrefix={`${idPrefix}-order`}
          formId={formId}
          value={value.order}
          onChange={(order) => onChange({ ...value, order })}
          disabled={!editable}
          errors={{
            medication: errors.medication,
            dose: errors.dose,
            frequency: errors.frequency,
            duration: errors.duration,
            deviationReason: errors.deviationReason,
          }}
        />
        <PlannedMedicationExtraFields
          idPrefix={`${idPrefix}-order`}
          order={value.order}
          strength={value.strength}
          plan={value.plan}
          onStrengthChange={(strength) => onChange({ ...value, strength })}
          onPlanChange={(plan) => onChange({ ...value, plan })}
          disabled={!editable}
        />
        {value.planChoice === "changed" ? (
          <label className="flex flex-col gap-xs text-sm">
            <span className="text-textSecondary">
              Medicinsk motivering till ändringen
            </span>
            <textarea
              value={value.changeReason}
              onChange={(e) =>
                onChange({ ...value, changeReason: e.target.value })
              }
              rows={2}
              maxLength={2000}
              disabled={disabled}
              className="rounded-md border border-surfaceMuted p-sm text-sm focus:border-brand focus:outline-none"
            />
            {errors.changeReason ? (
              <span className="text-xs text-danger">{errors.changeReason}</span>
            ) : null}
          </label>
        ) : null}
      </fieldset>

      <fieldset
        id={fieldDomId(formId, "prescriptionNeed")}
        tabIndex={-1}
        className="flex flex-col gap-xs"
      >
        <legend className="mb-xs text-sm font-semibold">Receptstatus</legend>
        {TRANSFER_RECEPT_PRESCRIPTION_NEEDS.map((n) => (
          <label key={n} className="flex items-start gap-sm text-sm">
            <input
              type="radio"
              name={`${idPrefix}-prescription-need`}
              className="mt-[3px] accent-brand"
              checked={value.need === n}
              disabled={disabled}
              onChange={() => onChange({ ...value, need: n })}
            />
            <span>
              <span className="text-textPrimary">
                {TRANSFER_RECEPT_PRESCRIPTION_NEED_LABEL[n]}
              </span>
              <span className="block text-xs text-textSecondary">
                {NEED_HINT[n]}
              </span>
            </span>
          </label>
        ))}
        {errors.prescriptionNeed ? (
          <p className="text-xs text-danger">{errors.prescriptionNeed}</p>
        ) : null}
      </fieldset>
      <p className="text-xs text-textSecondary">
        Det du bekräftar eller ändrar sparas som planerad läkemedelsbehandling
        när du godkänner receptövertaget och behöver inte skrivas in igen.{" "}
        {TRANSFER_RECEPT_SAVED_WITH_COMPLETION}
      </p>
    </section>
  );
}
